
import { useState } from "react";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Filter, Tag, Star, CheckCheck, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

interface VendorFiltersProps {
  onCategoryChange: (category: string) => void;
  onSatisfactionChange: (rating: number) => void;
  onFulfillmentChange: (rate: number) => void;
  onPriceRangeChange: (range: [number, number]) => void;
  categories: string[];
}

const VendorFilters = ({
  onCategoryChange,
  onSatisfactionChange,
  onFulfillmentChange,
  onPriceRangeChange,
  categories,
}: VendorFiltersProps) => {
  const [minPrice, setMinPrice] = useState<string>("0");
  const [maxPrice, setMaxPrice] = useState<string>("10000");
  const [isExpanded, setIsExpanded] = useState(true);
  const isMobile = useIsMobile();

  const handlePriceSubmit = () => {
    onPriceRangeChange([parseInt(minPrice) || 0, parseInt(maxPrice) || 10000]);
  };

  return (
    <Card className="sticky top-24">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center">
            <Filter className="h-4 w-4 mr-2" />
            Vendor Filters
          </span>
          {!isMobile && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setIsExpanded(!isExpanded)}
              aria-expanded={isExpanded}
              aria-controls="filter-content"
            >
              {isExpanded ? "Hide" : "Show"}
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent id="filter-content" className={isExpanded ? "space-y-5" : "hidden"}>
        <div>
          <label id="category-label" className="text-sm font-medium mb-2 block flex items-center">
            <Tag className="h-4 w-4 mr-2" />
            Business Category
          </label>
          <Select onValueChange={onCategoryChange} defaultValue="all">
            <SelectTrigger aria-labelledby="category-label">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category} value={category.toLowerCase()}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label id="satisfaction-label" className="text-sm font-medium mb-2 block flex items-center">
            <Star className="h-4 w-4 mr-2 fill-yellow-400 text-yellow-400" />
            Minimum Customer Satisfaction
          </label>
          <div className="pt-2 px-2">
            <Slider
              defaultValue={[0]}
              max={5}
              step={0.5}
              onValueChange={(values) => onSatisfactionChange(values[0])}
              aria-labelledby="satisfaction-label"
            />
            <div className="flex justify-between mt-1 text-xs text-muted-foreground">
              <span>0</span>
              <span>1</span>
              <span>2</span>
              <span>3</span>
              <span>4</span>
              <span>5</span>
            </div>
          </div>
        </div>

        <div>
          <label id="fulfillment-label" className="text-sm font-medium mb-2 block flex items-center">
            <CheckCheck className="h-4 w-4 mr-2" />
            Minimum Fulfillment Rate (%)
          </label>
          <div className="pt-2 px-2">
            <Slider
              defaultValue={[0]}
              max={100}
              step={5}
              onValueChange={(values) => onFulfillmentChange(values[0])}
              aria-labelledby="fulfillment-label"
            />
            <div className="flex justify-between mt-1 text-xs text-muted-foreground">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
          </div>
        </div>

        <div>
          <label id="price-label" className="text-sm font-medium mb-2 block flex items-center">
            <DollarSign className="h-4 w-4 mr-2" />
            Price Range
          </label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              placeholder="Min"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              className="w-1/2"
              aria-label="Minimum price"
            />
            <span>-</span>
            <Input
              type="number"
              placeholder="Max"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              className="w-1/2"
              aria-label="Maximum price"
            />
          </div>
          <Button 
            size="sm" 
            onClick={handlePriceSubmit} 
            className="mt-2 w-full"
          >
            Apply Price Filter
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default VendorFilters;
